/**
 * Segment-aware primary navigation items.
 *
 * Once a user's segment is known (school_student / college_student / working_professional),
 * the global nav surfaces that segment's own sub-pages instead of the generic bar.
 * Anonymous / unknown-segment users still see the legacy fallback (Home · Quiz · Explore · Compare · Paths · Hub).
 */
import {
  Home,
  Brain,
  LayoutDashboard,
  GraduationCap,
  BookOpen,
  Building2,
  FileText,
  Briefcase,
  Calendar,
  TrendingUp,
  Sparkles,
  Target,
  type LucideIcon,
} from "lucide-react";

export type Segment =
  | "school_student"
  | "college_student"
  | "working_professional";

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Short label used on tight mobile bars. */
  shortLabel?: string;
}

// ───────────────────────── Core (always visible) ─────────────────────────

export const CORE_NAV_ITEMS: NavItem[] = [
  { path: "/", label: "Home", icon: Home },
  { path: "/quiz", label: "Quiz", icon: Brain },
  { path: "/career-hub", label: "Hub", icon: LayoutDashboard },
];

// ─────────────────────── Segment add-on items ───────────────────────────

export const SCHOOL_NAV_ITEMS: NavItem[] = [
  { path: "/career-hub/stream-guidance", label: "Stream", icon: GraduationCap, shortLabel: "Stream" },
  { path: "/career-hub/exam-prep", label: "Exam Prep", icon: BookOpen, shortLabel: "Exams" },
  { path: "/career-hub/colleges", label: "Colleges", icon: Building2, shortLabel: "Colleges" },
  { path: "/career-hub/admissions", label: "Admissions", icon: FileText, shortLabel: "Admit" },
];

export const COLLEGE_NAV_ITEMS: NavItem[] = [
  { path: "/career-hub/internships", label: "Internships", icon: Briefcase, shortLabel: "Intern" },
  { path: "/career-hub/placement-prep", label: "Placement Prep", icon: Target, shortLabel: "Placement" },
  { path: "/career-hub/higher-education", label: "Higher Ed", icon: GraduationCap, shortLabel: "Higher Ed" },
  { path: "/career-hub/events", label: "Events", icon: Calendar, shortLabel: "Events" },
];

export const WORKING_NAV_ITEMS: NavItem[] = [
  { path: "/studio", label: "Studio", icon: Sparkles, shortLabel: "Studio" },
  { path: "/studio/skill-gaps", label: "Skill Gaps", icon: TrendingUp, shortLabel: "Skills" },
  { path: "/studio/resume-builder", label: "Resume", icon: FileText, shortLabel: "Resume" },
  { path: "/studio/jobs", label: "Jobs", icon: Briefcase, shortLabel: "Jobs" },
];

// ───────────────────── Fallback (segment unknown) ───────────────────────
// Kept intentionally minimal — when a user has no segment yet (anonymous or
// pre-quiz), we surface ONLY Home + Quiz so the nav doesn't imply a product
// (Explore / Compare / Paths) the user hasn't picked yet. Once the segment
// is known, getNavItemsForSegment() swaps in the segment-specific items.

export const FALLBACK_NAV_ITEMS: NavItem[] = [
  { path: "/", label: "Home", icon: Home },
  { path: "/quiz", label: "Quiz", icon: Brain },
];

// ───────────────────────────── Resolver ─────────────────────────────────

const SEGMENT_ITEMS: Record<Segment, NavItem[]> = {
  school_student: SCHOOL_NAV_ITEMS,
  college_student: COLLEGE_NAV_ITEMS,
  working_professional: WORKING_NAV_ITEMS,
};

export interface NavOptions {
  /** When true, cap to ~6 items (Home + Quiz + first 3 segment items + Hub). */
  mobile?: boolean;
}

/**
 * Returns the navigation items to render for the current user.
 *
 * - `segment === null/undefined` → legacy fallback bar.
 * - Mobile: Home, Quiz, first 3 segment items, Hub.
 * - Desktop/tablet: Home, Quiz, all segment items, Hub.
 */
export function getNavItemsForSegment(
  segment: Segment | null | undefined,
  options: NavOptions = {},
): NavItem[] {
  if (!segment || !SEGMENT_ITEMS[segment]) {
    return FALLBACK_NAV_ITEMS;
  }
  const segmentItems = SEGMENT_ITEMS[segment];
  const home = CORE_NAV_ITEMS[0];
  const quiz = CORE_NAV_ITEMS[1];
  const hub = CORE_NAV_ITEMS[2];

  const middle = options.mobile ? segmentItems.slice(0, 3) : segmentItems;
  return [home, quiz, ...middle, hub];
}

export function isSegment(value: unknown): value is Segment {
  return (
    value === "school_student" ||
    value === "college_student" ||
    value === "working_professional"
  );
}
